import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { updateUser, fetchUsers } from '../../store/slices/usersSlice';
import { fetchRoles } from '../../store/slices/rolesSlice';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'remixicon/fonts/remixicon.css';
import Swal from 'sweetalert2';

const EditUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { users, isLoading, error } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.roles);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const existingUser = users.find((u) => String(u.id) === String(id));

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    roles: [],
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch roles and users on mount
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchUsers());
  }, [dispatch]);

  // Pre-fill form once user data is available
  useEffect(() => {
    if (existingUser) {
      setFormData({
        name: existingUser.name || '',
        email: existingUser.email || '',
        password: '',
        password_confirmation: '',
        roles: existingUser.roles ? existingUser.roles.map((r) => Number(r.id)) : [],
      });
    }
  }, [existingUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleRoleToggle = (roleId) => {
    const id = Number(roleId);
    if (!id || isNaN(id) || id <= 0) return;

    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(id)
        ? prev.roles.filter((r) => r !== id)
        : [...prev.roles, id],
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = 'Please enter a valid email address';

    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters';

    if (formData.password !== formData.password_confirmation)
      errors.password_confirmation = 'Passwords do not match';

    // Check roles
    if (formData.roles.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'No Roles Selected',
        text: 'Please select at least one role for the user.',
      });
      return false;
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Form Error',
        text: 'Please fix the highlighted fields.',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const submitData = { ...formData };
      if (!submitData.password) {
        delete submitData.password;
        delete submitData.password_confirmation;
      }
      if (submitData.roles.length === 0) delete submitData.roles;

      await dispatch(updateUser({ id, userData: submitData })).unwrap();
      await dispatch(fetchUsers());
      navigate('/users');
    } catch (err) {
      setFormErrors({ general: err.message || 'Failed to update user' });
    }
  };

  const isAllRolesSelected = roles && formData.roles.length === roles.length;

  if (!existingUser && !isLoading) {
    return (
      <MainLayout>
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="alert alert-warning mt-4">User not found.</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-center align-items-center">
            <h4 className="mb-0">Edit User</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} id="editUserForm">
              {/* {formErrors.general && <div className="alert alert-danger">{formErrors.general}</div>}
              {error && <div className="alert alert-danger">{error}</div>} */}

              <div className="row">
                {/* Left Column - User Details */}
                <div className="col-xl-6">
                  <div className="mb-4 d-flex justify-content-between align-items-center border-bottom pb-3">
                    <h6 className="mb-0 badge bg-primary text-white">
                      <i className="ri-user-line me-2 text-white"></i> User Details
                    </h6>
                  </div>

                  <div className="form-floating form-floating-outline mb-3">
                    <input
                      type="text"
                      className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="name">Full Name</label>
                    {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                  </div>

                  <div className="form-floating form-floating-outline mb-3">
                    <input
                      type="email"
                      className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="email">Email</label>
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>

                  <div className="form-floating form-floating-outline mb-3 position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                      id="password"
                      name="password"
                      placeholder="New Password (optional)"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="password">New Password (optional)</label>
                    {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}

                    {/* Eye button */}
                    <i
                      className={`ri-eye${showPassword ? "-line" : "-off-line"} position-absolute top-50 end-0 translate-middle-y me-3`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowPassword((prev) => !prev)}
                    ></i>
                  </div>

                  <div className="form-floating form-floating-outline mb-3 position-relative">
                    <input
                      type={showPasswordConfirmation ? "text" : "password"}
                      className={`form-control ${formErrors.password_confirmation ? "is-invalid" : ""}`}
                      id="password_confirmation"
                      name="password_confirmation"
                      placeholder="Confirm Password"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="password_confirmation">Confirm Password</label>
                    {formErrors.password_confirmation && (
                      <div className="invalid-feedback">{formErrors.password_confirmation}</div>
                    )}

                    {/* Eye button */}
                    <i
                      className={`ri-eye${showPasswordConfirmation ? "-line" : "-off-line"} position-absolute top-50 end-0 translate-middle-y me-3`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                    ></i>
                  </div>
                </div>

                {/* Right Column - Role Selection */}
                <div className="col-xl-6">
                  <div className="mb-4 d-flex justify-content-between align-items-center border-bottom pb-3">
                    <h6 className="mb-0 badge bg-light text-primary">
                      <i className="ri-shield-user-line me-2 text-primary"></i> Available Roles
                    </h6>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="selectAllRoles"
                        checked={isAllRolesSelected}
                        onChange={() => {
                          if (isAllRolesSelected) {
                            setFormData((prev) => ({ ...prev, roles: [] }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              roles: roles.map((role) => Number(role.id)),
                            }));
                          }
                        }}
                      />
                      <label className="form-check-label small fw-medium" htmlFor="selectAllRoles">
                        Select All
                      </label>
                    </div>
                  </div>

                  <div
                    className="d-flex flex-column gap-3 pe-2"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {roles && roles.length > 0 ? (
                      roles.map((role) => {
                        const isSelected = formData.roles.includes(Number(role.id));
                        return (
                          <div
                            key={role.id}
                            className={`card border transition-all ${isSelected ? 'border-primary bg-light' : 'border-light'
                              }`}
                            style={{ transition: 'all 0.2s ease-in-out', cursor: 'pointer' }}
                            onClick={() => handleRoleToggle(role.id)}
                          >
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center mb-2">
                                <input
                                  className="form-check-input me-3"
                                  type="checkbox"
                                  id={`role-${role.id}`}
                                  checked={isSelected}
                                  onChange={() => handleRoleToggle(role.id)}
                                  style={{ cursor: 'pointer' }}
                                />
                                <label
                                  className="form-check-label fw-semibold text-capitalize mb-0 flex-grow-1"
                                  htmlFor={`role-${role.id}`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {role.name}
                                </label>
                              </div>
                              <p className="small text-muted mb-0">
                                User will have {role.name.toLowerCase()} privileges and access rights.
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4">
                        <i className="ri-shield-line fs-1 text-muted mb-3"></i>
                        <p className="text-muted mb-0">No roles available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="row mt-5 pt-3 border-top">
                <div className="col-1 pe-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 btn-sm"
                    onClick={() => navigate('/users')}
                  >
                    <i className="ri-arrow-left-line me-1"></i>Back
                  </button>
                </div>
                <div className="col-2">
                  <button type="submit" className="btn btn-primary w-100 btn-sm" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Update
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line me-1"></i>Update
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditUser;
